import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { OpenInNew, PictureAsPdf } from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import {
    DownloadResult,
    openTechnicalDocumentation,
    prefetchTechnicalDocumentation,
    releaseTechnicalDocumentation,
} from "../../../utils/download-utils";
import { translate } from "../../../translate/translate";
import { useDocumentTitle } from "../../../contexts/document-title-context";

import imgLogin from "../../../../assets/images/help/manual-planmat-login.png";
import imgHome from "../../../../assets/images/help/manual-planmat-home.png";
import imgMenu from "../../../../assets/images/help/manual-planmat-menu.png";
import imgUsuariosLista from "../../../../assets/images/help/manual-planmat-usuarios-lista.png";
import imgUsuariosForm from "../../../../assets/images/help/manual-planmat-usuarios-form.png";
import imgGruposLista from "../../../../assets/images/help/manual-planmat-grupos-lista.png";
import imgGruposForm from "../../../../assets/images/help/manual-planmat-grupos-form.png";
import imgClientesLista from "../../../../assets/images/help/manual-planmat-clientes-lista.png";
import imgClientesForm from "../../../../assets/images/help/manual-planmat-clientes-form.png";
import imgBomLista from "../../../../assets/images/help/manual-planmat-bom-lista.png";
import imgBomEdicao from "../../../../assets/images/help/manual-planmat-bom-edicao.png";
import imgEstoque from "../../../../assets/images/help/manual-planmat-estoque.png";
import imgForecastLista from "../../../../assets/images/help/manual-planmat-forecast-lista.png";
import imgForecastDetalhe from "../../../../assets/images/help/manual-planmat-forecast-detalhe.png";
import imgPoLista from "../../../../assets/images/help/manual-planmat-po-lista.png";
import imgImportData from "../../../../assets/images/help/manual-planmat-import-data.png";
import imgImportXls from "../../../../assets/images/help/manual-planmat-import-xls.png";
import imgImportSap from "../../../../assets/images/help/manual-planmat-import-sap.png";
import imgAnalysisLista from "../../../../assets/images/help/manual-planmat-analysis-lista.png";
import imgAnalysisNovo from "../../../../assets/images/help/manual-planmat-analysis-novo.png";
import imgAnalysisProcessing from "../../../../assets/images/help/manual-planmat-analysis-processing.png";
import imgAnalysisError from "../../../../assets/images/help/manual-planmat-analysis-error.png";
import imgGdsCalculation from "../../../../assets/images/help/manual-planmat-gds-calculation.png";
import imgGdsSimulation from "../../../../assets/images/help/manual-planmat-gds-simulation.png";
import imgAnalysisTabs from "../../../../assets/images/help/manual-planmat-analysis-tabs.png";
import imgSimulationBom from "../../../../assets/images/help/manual-planmat-simulation-bom.png";
import imgSimulationBomSubstituto from "../../../../assets/images/help/manual-planmat-simulation-bom-substituto.png";
import imgSimulationBomNovoItem from "../../../../assets/images/help/manual-planmat-simulation-bom-novo-item.png";
import imgMaterialsDemands from "../../../../assets/images/help/manual-planmat-materials-demands.png";
import imgMaterialsDemandsPreco from "../../../../assets/images/help/manual-planmat-materials-demands-preco.png";
import imgDashboard from "../../../../assets/images/help/manual-planmat-dashboard.png";
import imgDashboardWidgets from "../../../../assets/images/help/manual-planmat-dashboard-widgets.png";
import imgDashboardSap from "../../../../assets/images/help/manual-planmat-dashboard-sap.png";
import imgAiForecast from "../../../../assets/images/help/manual-planmat-ai-demand-forecast.png";

/** `child` marca um atalho para uma subseção: aparece indentado no sumário e não
 *  disputa o destaque com a seção que o contém. */
type Section = { id: string; title: string; child?: boolean };

const SECTIONS: Section[] = [
    { id: "introducao", title: "1. Apresentação" },
    { id: "acesso", title: "2. Acesso ao sistema" },
    { id: "menu-lateral", title: "3. Navegação geral" },
    { id: "controle-acesso", title: "4. Controle de acesso" },
    { id: "cadastros", title: "5. Cadastros básicos" },
    { id: "forecast", title: "6. Forecast" },
    { id: "po", title: "7. Ordens de Compra" },
    { id: "importar", title: "8. Importar dados" },
    { id: "analise", title: "9. Análise GDS" },
    { id: "materiais-demandas", title: "9.7 Itens e Demandas", child: true },
    { id: "simulacao-bom", title: "9.9 Simulação: aba BOM", child: true },
    { id: "dashboard", title: "10. Dashboard" },
    { id: "saude-sap", title: "10.4 Saúde das importações SAP", child: true },
    { id: "previsao-ia", title: "11. Previsão de demanda por IA" },
    { id: "boas-praticas", title: "12. Boas práticas de uso" },
    { id: "faq", title: "13. Perguntas frequentes" },
    { id: "glossario", title: "14. Glossário" },
    { id: "suporte", title: "15. Suporte" },
];

const Figure = ({ src, alt, caption }: { src: string; alt: string; caption: string }) => (
    <Box component="figure" sx={{ m: 0, my: 3 }}>
        <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
                maxWidth: "100%",
                height: "auto",
                display: "block",
                border: "1px solid #E0E0E0",
                borderRadius: 1,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
        />
        <Typography
            component="figcaption"
            variant="caption"
            sx={{ display: "block", mt: 1, color: "text.secondary", fontStyle: "italic" }}
        >
            {caption}
        </Typography>
    </Box>
);

const SectionBlock = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <Box component="section" id={id} sx={{ scrollMarginTop: "80px", mb: 6 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.75, textAlign: "justify" }}>
        {children}
    </Typography>
);

const SubTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mt: 4, mb: 1.5 }}>
        {children}
    </Typography>
);

/** Subseção com âncora própria, para ser alcançada pelos atalhos do sumário. */
const SubSection = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <Box id={id} sx={{ scrollMarginTop: "80px" }}>
        <SubTitle>{title}</SubTitle>
        {children}
    </Box>
);

const Note = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            borderLeft: "4px solid #1976D2",
            backgroundColor: "rgba(25, 118, 210, 0.06)",
            px: 2,
            py: 1.5,
            my: 2,
            borderRadius: "0 4px 4px 0",
        }}
    >
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            {children}
        </Typography>
    </Box>
);

const SimpleTable = ({
    head,
    rows,
}: {
    head: string[];
    rows: (string | React.ReactNode)[][];
}) => (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
        <Table size="small">
            <TableHead>
                <TableRow sx={{ backgroundColor: "#F5F6FA" }}>
                    {head.map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700 }}>
                            {h}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, ri) => (
                    <TableRow key={ri}>
                        {row.map((cell, ci) => (
                            <TableCell key={ci}>{cell}</TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const HelpComponent = () => {
    const { setDocumentTitle } = useDocumentTitle();
    const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);
    const [technicalDocumentationLoading, setTechnicalDocumentationLoading] = useState(false);
    const observer = useRef<IntersectionObserver | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const sidebarButtonRefs = useRef<Record<string, HTMLElement | null>>({});
    const isScrollingByClick = useRef(false);

    useEffect(() => {
        setDocumentTitle();
        return () => releaseTechnicalDocumentation();
    }, []);

    useEffect(() => {
        if (!contentRef.current) return;
        observer.current?.disconnect();
        observer.current = new IntersectionObserver(
            (entries) => {
                if (isScrollingByClick.current) return;
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length > 0) {
                    setActiveId(visible[0].target.id);
                }
            },
            { root: contentRef.current, rootMargin: "0px 0px -40% 0px", threshold: 0 }
        );
        // Subseções não são observadas: elas ficam dentro de uma seção e roubariam o
        // destaque do item pai no sumário.
        SECTIONS.filter(({ child }) => !child).forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.current?.observe(el);
        });
        return () => observer.current?.disconnect();
    }, [contentRef.current]);

    useEffect(() => {
        const btn = sidebarButtonRefs.current[activeId];
        if (btn) {
            btn.scrollIntoView({ block: "nearest" });
        }
    }, [activeId]);

    const scrollTo = (id: string) => {
        isScrollingByClick.current = true;
        setActiveId(id);
        const el = document.getElementById(id);
        const container = contentRef.current;
        if (el && container) {
            const elTop = el.getBoundingClientRect().top;
            const containerTop = container.getBoundingClientRect().top;
            const offset = elTop - containerTop + container.scrollTop - 16;
            container.scrollTo({ top: offset, behavior: "smooth" });
        }
        setTimeout(() => {
            isScrollingByClick.current = false;
        }, 800);
    };

    const TECHNICAL_DOCUMENTATION_MIN_LOADING_MS = 400;

    const handleTechnicalDocumentationFailure = (result: DownloadResult) => {
        if (result.blocked && result.blobUrl) {
            toast.info(
                <span>
                    {translate.t("technical_documentation_popup_blocked")}{" "}
                    <a href={result.blobUrl} target="_blank" rel="noopener noreferrer">
                        {translate.t("technical_documentation")}
                    </a>
                </span>
            );
            return;
        }
        toast.error(translate.t("technical_documentation_error"));
    };

    const handleOpenTechnicalDocumentation = async () => {
        const maybePromise = openTechnicalDocumentation();

        if (!(maybePromise instanceof Promise)) {
            if (!maybePromise.success) {
                handleTechnicalDocumentationFailure(maybePromise);
            }
            return;
        }

        setTechnicalDocumentationLoading(true);
        const startedAt = Date.now();
        const result = await maybePromise;
        const elapsedMs = Date.now() - startedAt;
        if (elapsedMs < TECHNICAL_DOCUMENTATION_MIN_LOADING_MS) {
            await new Promise((resolve) =>
                setTimeout(resolve, TECHNICAL_DOCUMENTATION_MIN_LOADING_MS - elapsedMs)
            );
        }
        setTechnicalDocumentationLoading(false);

        if (!result.success) {
            handleTechnicalDocumentationFailure(result);
        }
    };

    return (
        <section>
            <div className="floating-title" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <Typography variant="h5">
                    {translate.t("user_manual")}
                </Typography>
            </div>

            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={3}>
                    <Paper
                        variant="outlined"
                        sx={{
                            position: "sticky",
                            top: 16,
                            p: 1,
                            maxHeight: "calc(100vh - 140px)",
                            overflowY: "auto",
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{ pl: 2, color: "text.secondary", fontWeight: 700 }}
                        >
                            Sumário
                        </Typography>
                        <List dense disablePadding>
                            {SECTIONS.map((s) => (
                                <ListItem key={s.id} disablePadding>
                                    <ListItemButton
                                        ref={(el) => { sidebarButtonRefs.current[s.id] = el; }}
                                        selected={activeId === s.id}
                                        onClick={() => scrollTo(s.id)}
                                        sx={{
                                            borderRadius: 1,
                                            pl: s.child ? 4 : 2,
                                            py: s.child ? 0.25 : 0.5,
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(25, 118, 210, 0.12)",
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={s.title}
                                            primaryTypographyProps={{
                                                fontSize: s.child ? 12 : 13,
                                                color: s.child ? "text.secondary" : "text.primary",
                                                fontWeight: activeId === s.id ? 700 : 500,
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                            <Divider sx={{ my: 1 }} />
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={handleOpenTechnicalDocumentation}
                                    onMouseEnter={prefetchTechnicalDocumentation}
                                    onFocus={prefetchTechnicalDocumentation}
                                    disabled={technicalDocumentationLoading}
                                    aria-busy={technicalDocumentationLoading}
                                    sx={{
                                        borderRadius: 1,
                                        px: 2,
                                        py: 0.75,
                                        gap: 1,
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32, color: "error.main" }}>
                                        <PictureAsPdf fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={translate.t("technical_documentation")}
                                        primaryTypographyProps={{
                                            fontSize: 13,
                                            fontWeight: 500,
                                        }}
                                    />
                                    {technicalDocumentationLoading ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <OpenInNew fontSize="small" color="action" />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Paper ref={contentRef} sx={{ p: { xs: 2, md: 4 }, height: "calc(100vh - 140px)", overflowY: "auto" }}>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="body1" sx={{ lineHeight: 1.75, textAlign: "justify" }}>
                                Este manual descreve, do ponto de vista do usuário final, como operar o sistema
                                PlanMat — incluindo o acesso, os módulos do menu, os principais fluxos de trabalho,
                                boas práticas e perguntas frequentes.
                            </Typography>
                        </Box>

                        <SectionBlock id="introducao" title="1. Apresentação">
                            <SubTitle>1.1 O que é o PlanMat</SubTitle>
                            <Paragraph>
                                O PlanMat é um sistema de inteligência e planejamento de suprimentos desenvolvido para substituir controles manuais em planilhas pesadas, reduzir o tempo de processamento e diminuir o risco de erro humano nas análises de materiais.
                            </Paragraph>
                            <Paragraph>
                                O PlanMat cruza informações de estrutura de BOMs, previsão de demanda, estoque físico e pedidos de materiais e materiais em trânsito para projetar falta, risco de falta, cobertura, excesso de inventário e obsolência.
                            </Paragraph>
                            <Paragraph>
                                Na prática, o PlanMat ajuda a responder perguntas como:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Temos material suficiente para atender o forecast?</li>
                                <li>Quais componentes podem faltar nas próximas semanas?</li>
                                <li>O que está comprado, mas ainda não chegou?</li>
                                <li>Quais materiais estão em excesso ou obsoletos?</li>
                                <li>Qual ação precisa ser tomada por planejamento, compras ou gestão?</li>
                                <li>Quais materiais temos em estoque sem demanda?</li>
                            </Box>

                            <SubTitle>1.2 Para quem é o sistema</SubTitle>
                            <SimpleTable
                                head={["Perfil", "Uso principal"]}
                                rows={[
                                    [
                                        "Planejadores de compra de materiais",
                                        "Importam dados, revisam informações, criam análises GDS, simulam cenários e acompanham riscos de abastecimento."
                                    ],
                                    [
                                        "Compradores",
                                        "Consultam projeções de falta, excesso e materiais em trânsito para antecipar compras, renegociar entregas e priorizar fornecedores."
                                    ],
                                    [
                                        "Gestores",
                                        "Acompanham indicadores, riscos críticos, excesso de materiais e evolução das análises pelo dashboard."
                                    ],
                                    [
                                        "Administradores",
                                        "Gerenciam usuários, grupos de permissão e parâmetros de acesso."
                                    ]
                                ]}
                            />

                            <SubTitle>1.3 Requisitos para acesso</SubTitle>
                            <Paragraph>
                                Antes de acessar o PlanMat, confirme:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Você possui usuário ativo no sistema.</li>
                                <li>Você está conectado à rede ou VPN corporativa, quando necessário.</li>
                                <li>Seu perfil possui permissão para acessar os módulos que você precisa usar.</li>
                                <li>Você recebeu a URL oficial do ambiente correto, por exemplo, homologação ou produção.</li>
                            </Box>
                        </SectionBlock>

                        <SectionBlock id="acesso" title="2. Acesso ao sistema">
                            <SubTitle>2.1 Como fazer login</SubTitle>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Acesse a URL oficial do ambiente desejado.</li>
                                <li>Informe seu usuário e senha.</li>
                                <li>Se necessário, altere o idioma da interface pelo seletor PT / EN.</li>
                                <li>Clique em Entrar.</li>
                            </Box>
                            <Note>
                                Se as credenciais estiverem incorretas, o sistema exibirá uma mensagem abaixo do botão Entrar.
                            </Note>
                            <Figure src={imgLogin} alt="Tela de login" caption="Tela de login do PlanMat." />

                            <SubTitle>2.2 Esqueci minha senha</SubTitle>
                            <Paragraph>
                                O PlanMat não possui recuperação de senha pelo próprio usuário.
                            </Paragraph>
                            <Paragraph>
                                Caso esqueça sua senha, solicite o reset a um administrador do sistema. O administrador fará a redefinição de acesso e você deverá cadastrar uma nova senha, conforme a política interna de segurança.
                            </Paragraph>

                            <SubTitle>2.3 Tela inicial</SubTitle>
                            <Paragraph>
                                Após o login, o usuário é direcionado para a tela inicial. O layout padrão possui:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Menu lateral à esquerda, com os módulos operacionais.</li>
                                <li>Barra superior, com informações do usuário logado, notificações e opção de logout.</li>
                                <li>Área central, onde a página selecionada é exibida.</li>
                            </Box>
                            <Figure src={imgHome} alt="Tela inicial" caption="Tela inicial após o login." />
                        </SectionBlock>

                        <SectionBlock id="menu-lateral" title="3. Navegação geral">
                            <Paragraph>
                                O PlanMat possui duas áreas principais de navegação.
                            </Paragraph>

                            <SubTitle>3.1 Menu lateral</SubTitle>
                            <Paragraph>
                                O menu lateral concentra a operação diária do sistema. Nele ficam os módulos Dashboard, Análise, Previsão de demanda por IA, BOM, Cliente, Estoque, Forecast, Ordem de Compra, Importar Dados (via SAP e Planilhas) e Ajuda.
                            </Paragraph>
                            <Note>
                                O <strong>Dashboard</strong> passou a ter item próprio no topo do menu e é a porta de entrada recomendada do dia a dia. Já <strong>Itens e Demandas</strong> deixou de ser um item do menu: ele agora é uma <strong>aba dentro de cada análise</strong> — veja 9.7.
                            </Note>
                            <Figure src={imgMenu} alt="Menu lateral" caption="Barra lateral expandida, com os itens do módulo Geral." />

                            <SubTitle>3.2 Controle de acesso</SubTitle>
                            <Paragraph>
                                A área de Controle de acesso é destinada a administradores. Ela permite gerenciar usuários e grupos de permissão.
                            </Paragraph>

                            <SubTitle>3.3 Mapa de módulos</SubTitle>
                            <SimpleTable
                                head={["Módulo", "Rota", "Finalidade"]}
                                rows={[
                                    ["Dashboard", "/general/dashboard", "Acompanhar indicadores de cobertura, ordens de compra por status e próximas entregas."],
                                    ["Análise", "/general/analysis", "Criar, processar, consultar, simular e exportar análises GDS. Reúne as abas Resultado GDS e Itens e Demandas."],
                                    ["Previsão de demanda por IA", "/general/ai-demand-forecast", "Consultar a previsão contínua, tendência e outliers por Part Number ou modelo."],
                                    ["BOM", "/general/bom", "Cadastrar e manter estruturas de produto."],
                                    ["Cliente", "/general/customer", "Cadastrar clientes usados no forecast."],
                                    ["Estoque", "/general/stock", "Consultar estoque físico disponível."],
                                    ["Forecast", "/general/forecast", "Consultar e gerenciar previsões de demanda."],
                                    ["Ordem de Compra", "/general/po", "Consultar POs e materiais em trânsito."],
                                    ["Importar Dados", "/general/import-data", "Importar dados por planilha ou SAP."],
                                    ["Grupos", "/account/group", "Configurar perfis e permissões de acesso."],
                                    ["Usuários", "/account/user", "Cadastrar e gerenciar usuários."],
                                    ["Ajuda", "/general/help", "Consultar este manual."],
                                ]}
                            />
                        </SectionBlock>

                        <SectionBlock id="controle-acesso" title="4. Controle de acesso">
                            <SubTitle>4.1 Usuários</SubTitle>
                            <Paragraph>
                                Caminho na barra superior: Geral &gt; Controle de acesso.
                            </Paragraph>
                            <Paragraph>
                                Essa tela exibe a lista de usuários cadastrados. Normalmente, a listagem apresenta login, nome, e-mail, grupo, status e ações disponíveis.
                            </Paragraph>
                            <Paragraph>Principais ações:</Paragraph>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Consultar usuários cadastrados.</li>
                                <li>Filtrar ou buscar usuários.</li>
                                <li>Criar novo usuário.</li>
                                <li>Editar dados de um usuário existente.</li>
                                <li>Ativar ou desativar um usuário.</li>
                                <li>Redefinir acesso, quando permitido pela política interna.</li>
                            </Box>
                            <Paragraph>Para criar um usuário:</Paragraph>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Clique em Novo usuário.</li>
                                <li>Preencha os dados obrigatórios.</li>
                                <li>Selecione o grupo de permissão.</li>
                                <li>Salve o cadastro.</li>
                            </Box>
                            <Paragraph>Para editar um usuário:</Paragraph>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Localize o usuário na lista.</li>
                                <li>Abra o formulário de edição, conforme o padrão da tela.</li>
                                <li>Ajuste os dados necessários.</li>
                                <li>Salve as alterações.</li>
                            </Box>
                            <Note>
                                Apenas usuários com permissão administrativa devem acessar esse módulo.
                            </Note>
                            <Figure src={imgUsuariosLista} alt="Lista de usuários" caption="Lista de usuários cadastrados." />
                            <Figure src={imgUsuariosForm} alt="Formulário de usuário" caption="Formulário de criação/edição de usuário." />

                            <SubTitle>4.2 Grupos</SubTitle>
                            <Paragraph>
                                Caminho na barra superior: Geral &gt; Controle de acesso.
                            </Paragraph>
                            <Paragraph>
                                A tela de Grupos permite criar e manter perfis de permissão. Cada grupo define quais menus e funcionalidades os usuários vinculados podem acessar.
                            </Paragraph>
                            <Paragraph>Use essa tela para:</Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Criar grupos de acesso.</li>
                                <li>Alterar permissões de um grupo existente.</li>
                                <li>Consultar quantos usuários estão vinculados a cada grupo.</li>
                                <li>Bloquear o acesso a módulos que não fazem parte da rotina de um perfil.</li>
                            </Box>
                            <Figure src={imgGruposLista} alt="Lista de grupos" caption="Lista de grupos de permissão." />
                            <Figure src={imgGruposForm} alt="Formulário de grupo" caption="Edição de grupo com seleção de menus permitidos." />
                        </SectionBlock>

                        <SectionBlock id="cadastros" title="5. Cadastros básicos">
                            <SubTitle>5.1 Clientes</SubTitle>
                            <Paragraph>
                                Caminho: Geral &gt; Cliente
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/customer
                            </Paragraph>
                            <Paragraph>
                                O cadastro de clientes serve como referência para as previsões de demanda.
                            </Paragraph>
                            <Paragraph>Ações comuns:</Paragraph>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Consultar clientes cadastrados.</li>
                                <li>Criar novo cliente.</li>
                                <li>Editar cliente existente.</li>
                            </Box>
                            <Note>
                                Ao importar forecast vindo de fonte integrada, o sistema usa o cliente já cadastrado no sistema para fazer o vínculo; se ele não existir, esse novo cliente será cadastrado automaticamente no sistema.
                            </Note>
                            <Figure src={imgClientesLista} alt="Lista de clientes" caption="Listagem de clientes cadastrados." />
                            <Figure src={imgClientesForm} alt="Formulário de cliente" caption="Formulário de cadastro de novo cliente." />

                            <SubTitle>5.2 BOM</SubTitle>
                            <Paragraph>
                                Caminho: Geral &gt; BOM
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/bom
                            </Paragraph>
                            <Paragraph>
                                A BOM representa a estrutura de componentes de um produto final. É uma das bases mais importantes para o cálculo GDS.
                            </Paragraph>
                            <Paragraph>Para acessar as BOMs importadas no sistema:</Paragraph>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Acesse o módulo BOM.</li>
                                <li>Pesquise pelo nome da BOM ou pelo Part Number.</li>
                                <li>Ao identificar a BOM, dê duplo clique para abrir seu detalhe e ver todos os componentes da estrutura.</li>
                            </Box>
                            <Paragraph>Campos comuns em uma linha de BOM:</Paragraph>
                            <SimpleTable
                                head={["Campo", "Descrição"]}
                                rows={[
                                    ["Nível", "Indica o nivel na BOM"],
                                    ["Item", "Indica Número ou referência de agrupamento da linha."],
                                    ["Part Number", "Código do PN na fábrica"],
                                    ["Tipo do Material", "ZHLB(Produto Semi-Acabado personalizado ) , ZROH (Matéria-Prima personalizada )"],
                                    ["Descrição", "Descrição do componente na fábrica"],
                                    ["Tipo", "Perfeito ou Imperfeito ou Phantom"],
                                    ["Qtde. de uso", "Quantidade de uso para montar 1un da BOM"],
                                    ["Prioridade de compra", "Indica se o componente é primário ou alternativo para uso na BOM."],
                                    ["Prioridade de uso", "indica qual a sua prioridade de uso no consumo da BOM"],
                                    ["Unid. de medida", "Unidade de medida do componente"],
                                ]}
                            />
                            <Figure src={imgBomLista} alt="Lista de BOMs" caption="Listagem de BOMs cadastradas." />
                            <Figure src={imgBomEdicao} alt="Edição de BOM" caption="Tela de detalhe e componentes da BOM." />
                            <Note>
                                Componentes marcados como Phantom (fantasma) não possuem estoque próprio e têm sua demanda explodida diretamente para os componentes filhos da sua respectiva estrutura.
                            </Note>

                            <SubTitle>5.3 Estoque</SubTitle>
                            <Paragraph>
                                Caminho: Geral &gt; Estoque
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/stock
                            </Paragraph>
                            <Paragraph>
                                A tela de Estoque permite consultar a quantidade física disponível de cada componente. Em geral, os dados de estoque são importados de sistemas oficiais ou planilhas controladas. A edição manual deve seguir a política definida pela operação.
                            </Paragraph>
                            <Paragraph>Use essa tela para verificar:</Paragraph>
                            <SimpleTable
                                head={["Campo", "Descrição"]}
                                rows={[
                                    ["Part Number", "Código do PN na fábrica"],
                                    ["Tipo do Material", "ZHLB(Produto Semi-Acabado personalizado ) , ZROH (Matéria-Prima personalizada )"],
                                    ["Descrição", "Descrição do componente na fábrica"],
                                    ["BOM", "Quais BOMs esse componente é utilizado"],
                                    ["OnHand", "Quantidade fisicamente disponível em estoque."],
                                    ["Fornecedor", "Indica qual o fornece"],
                                    ["Fabricante", "Indica qual o fabricante"],
                                    ["Obsoleto?", "Indica se o componente é obsoleto por não ter demanda"],
                                    ["Modificado em", "Indica a data que a informação foi atualizada"],
                                ]}
                            />
                            <Figure src={imgEstoque} alt="Estoque" caption="Consulta de estoque atual." />
                        </SectionBlock>

                        <SectionBlock id="forecast" title="6. Forecast">
                            <Paragraph>
                                Caminho: Geral &gt; Forecast
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/forecast
                            </Paragraph>
                            <Paragraph>
                                O Forecast representa a previsão de demanda por cliente usada nas análises de disponibilidade.
                            </Paragraph>

                            <SubTitle>6.1 Consultar forecasts</SubTitle>
                            <Paragraph>
                                A tela inicial mostra as previsões existentes, normalmente agrupadas por cliente, versão e período.
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Consultar forecasts importados.</li>
                                <li>Ver versões disponíveis (novas importações criam novas versões).</li>
                                <li>Abrir o detalhe de uma previsão.</li>
                                <li>Confirmar se a previsão correta será usada na análise.</li>
                            </Box>
                            <Figure src={imgForecastLista} alt="Lista de forecasts" caption="Lista de forecasts por cliente e versão." />

                            <SubTitle>6.2 Detalhar uma previsão</SubTitle>
                            <Paragraph>
                                Ao abrir um forecast, o sistema apresenta os itens previstos, quantidades e datas relacionadas. Verifique especialmente:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Cliente.</li>
                                <li>Versão.</li>
                                <li>Produto ou componente.</li>
                                <li>Quantidade prevista.</li>
                                <li>Data de entrega ou período de demanda.</li>
                            </Box>
                            <Figure src={imgForecastDetalhe} alt="Detalhe de forecast" caption="Detalhe de uma previsão de forecast." />

                            <SubTitle>6.3 Importação automática</SubTitle>
                            <Paragraph>
                                O Planmat está configurado para se atualizar automaticamente uma vez por semana na madrugada de terça feira. Neste caso, o sistema importa as forecasts automaticamente no dia e hora definidos pela operação. Após a importação, uma nova versão é criada e pode ser usada nas análises.
                            </Paragraph>
                            <Paragraph>
                                Caso precise forçar uma importação manual, use o módulo <strong>Importar Dados</strong>.
                            </Paragraph>
                        </SectionBlock>

                        <SectionBlock id="po" title="7. Ordens de Compra">
                            <Paragraph>
                                Caminho: Geral &gt; Ordem de Compra
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/po
                            </Paragraph>
                            <Paragraph>
                                A tela de Ordens de Compra apresenta os materiais em trânsito considerados no cálculo de disponibilidade.
                            </Paragraph>
                            <Paragraph>Cada linha normalmente exibe:</Paragraph>
                            <SimpleTable
                                head={["Campo", "Descrição"]}
                                rows={[
                                    ["Status", "Pode ser Aguardando fatura, Data expirada , Material em trânsito "],
                                    ["Data de chegada", "Indica data prevista para chegada do material"],
                                    ["Part Number", "Código do PN na fábrica"],
                                    ["Tipo do Material", "ZHLB(Produto Semi-Acabado personalizado ) , ZROH (Matéria-Prima personalizada )"],
                                    ["Descrição", "Descrição do componente na fábrica"],
                                    ["Fornecedor", "Indica qual o fornecedor do componente"],
                                    ["PO", "Indica Ordem de compra com itens em trânsito."],
                                    ["Invoice", "indica qual o número da invoice"],
                                    ["Quantidade", "indica a quantidade pedida"],
                                    ["Modificado em", "Indica a data que a informação foi atualizada"],
                                ]}
                            />
                            <Note>
                                O cálculo GDS considera como material em trânsito as quantidades pendentes das ordens de compra cujas datas de entrega estejam dentro da janela temporal analisada.
                            </Note>
                            <Figure src={imgPoLista} alt="Lista de POs" caption="Lista de ordens de compra com itens em trânsito." />
                        </SectionBlock>

                        <SectionBlock id="importar" title="8. Importar dados">
                            <Paragraph>
                                Caminho: Geral &gt; Importar Dados
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/import-data
                            </Paragraph>
                            <Paragraph>
                                O módulo de importação centraliza a entrada de dados no PlanMat. A tela apresenta os tipos de importação disponíveis (planilhas ou SAP) e o status das últimas execuções.
                            </Paragraph>
                            <Figure src={imgImportData} alt="Importar dados" caption="Hub de importação de dados." />

                            <SubTitle>8.1 Importação por planilha</SubTitle>
                            <Paragraph>
                                Caminho: Importar Dados &gt; Planilha
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/import-spreadsheets
                            </Paragraph>
                            <Paragraph>Passo a passo:</Paragraph>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Selecione o tipo de dado que será importado, por exemplo forecast, estoque, BOM ou PO.</li>
                                <li>Baixe o template, se for a primeira importação ou se houver dúvida sobre o formato.</li>
                                <li>Preencha a planilha conforme o modelo.</li>
                                <li>Envie o arquivo em formato .xlsx.</li>
                                <li>Clique em Importar.</li>
                                <li>Acompanhe o status da execução.</li>
                                <li>Em caso de erro, baixe o relatório de erros e corrija a planilha antes de tentar novamente.</li>
                            </Box>
                            <Paragraph>Cuidados importantes:</Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Não altere nomes de colunas do template.</li>
                                <li>Confira part numbers, datas e unidades.</li>
                                <li>Evite linhas duplicadas.</li>
                                <li>Use o relatório de erro para corrigir a causa, não apenas reenviar o mesmo arquivo.</li>
                            </Box>
                            <Figure src={imgImportXls} alt="Importação via planilha" caption="Tela de importação via planilha." />

                            <SubTitle>8.2 Importação por SAP</SubTitle>
                            <Paragraph>
                                Caminho: Importar Dados &gt; SAP
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/import-sap
                            </Paragraph>
                            <Paragraph>
                                Quando a integração SAP estiver habilitada, o sistema permite importar dados oficiais diretamente da origem configurada.
                            </Paragraph>
                            <Paragraph>Antes de importar:</Paragraph>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Confirme que a VPN ou rede necessária está ativa.</li>
                                <li>Selecione o tipo de dado.</li>
                                <li>Informe planta 451N,  materiais, quando solicitado.</li>
                                <li>Clique em Importar do SAP.</li>
                                <li>Acompanhe o progresso da execução.</li>
                            </Box>
                            <Paragraph>
                                Se houver falha de conexão, registre o ID da execução e acione o suporte ou o time de infraestrutura.
                            </Paragraph>
                            <Figure src={imgImportSap} alt="Importação via SAP" caption="Tela de importação via SAP." />
                        </SectionBlock>

                        <SectionBlock id="analise" title="9. Análise GDS">
                            <Paragraph>
                                Caminho: Geral &gt; Análise
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/analysis
                            </Paragraph>
                            <Paragraph>
                                A Análise GDS é o fluxo principal do PlanMat. Ela projeta a disponibilidade futura dos componentes cruzando forecast de demanda, estruturas de BOM, estoque atual e ordens de compra ativas.
                            </Paragraph>

                            <SubTitle>9.1 Consultar análises</SubTitle>
                            <Paragraph>
                                A tela inicial exibe as análises existentes, com nome, período, status e ações.
                            </Paragraph>
                            <Paragraph>Status comuns:</Paragraph>
                            <SimpleTable
                                head={["Status", "Significado"]}
                                rows={[
                                    ["Finalizando", "Os resultados já foram calculados e estão sendo preparados para liberação. Ainda não é possível abrir detalhes ou exportar."],
                                    ["aguardando", "A análise foi solicitada e está na fila para iniciar o processamento."],
                                    ["Concluída", "A análise foi processada e os resultados estão disponíveis para consulta e exportação."],
                                    ["Erro no cállculo", "A análise não foi concluída porque ocorreu uma falha durante o processamento dos dados."],
                                    ["Cancelado", "Análise cancelada"],
                                ]}
                            />
                            <Paragraph>
                                Para excluir análises, selecione uma ou mais linhas e clique em Deletar. Confirme a ação somente se tiver certeza, pois a exclusão pode ser definitiva.
                            </Paragraph>
                            <Figure src={imgAnalysisLista} alt="Lista de análises" caption="Listagem de análises do sistema." />

                            <SubTitle>9.2 Criar nova análise</SubTitle>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Clique em Nova análise.</li>
                                <li>Informe o nome da análise.</li>
                                <li>Defina a data inicial e a data final do horizonte.</li>
                                <li>Selecione um ou mais forecasts.</li>
                                <li>Clique em Salvar.</li>
                                <li>Aguarde a entrada da análise na fila de processamento.</li>
                            </Box>
                            <Note>
                                Boa prática: use nomes claros, com período e cliente ou contexto da análise. Exemplo: 2026 05, Cliente A, revisão mensal.
                            </Note>
                            <Figure src={imgAnalysisNovo} alt="Nova análise" caption="Formulário de criação de análise." />

                            <SubTitle>9.3 Acompanhar processamento</SubTitle>
                            <Paragraph>
                                Durante o processamento, o sistema pode exibir:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Barra de progresso.</li>
                                <li>Etapa atual.</li>
                                <li>Mensagens de status.</li>
                                <li>Estimativa ou indicação de fila.</li>
                            </Box>
                            <Paragraph>
                                Etapas comuns incluem carregar BOMs, calcular demanda, projetar estoque, gerar resultados e preparar exportações.
                            </Paragraph>
                            <Figure src={imgAnalysisProcessing} alt="Processamento de análise" caption="Tela de processamento em tempo real." />

                            <SubTitle>9.4 Tratar erros de análise</SubTitle>
                            <Paragraph>
                                Quando uma análise falha, o sistema exibe a causa do erro e, quando possível, os itens relacionados. Exemplos de causas:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>BOM não encontrada para um produto.</li>
                                <li>Componente inexistente ou inativo.</li>
                                <li>Forecast com dados inconsistentes.</li>
                                <li>PO com data ou unidade inválida.</li>
                                <li>Falha de integração ou importação.</li>
                            </Box>
                            <Paragraph>Ajuste a causa informada e execute a análise novamente.</Paragraph>
                            <Figure src={imgAnalysisError} alt="Erros de análise" caption="Tela de detalhamento de erros." />

                            <SubTitle>9.5 As abas da análise</SubTitle>
                            <Paragraph>
                                Ao abrir uma análise, o conteúdo é organizado em duas abas, exibidas logo abaixo do título:
                            </Paragraph>
                            <SimpleTable
                                head={["Aba", "O que mostra"]}
                                rows={[
                                    ["Resultado GDS", "A projeção de disponibilidade por componente, semana a semana, com exportação e acesso à simulação."],
                                    ["Itens e Demandas", "A demanda consolidada dos componentes daquela análise, com preço unitário, valor total e cobertura. Detalhada em 9.7."],
                                ]}
                            />
                            <Paragraph>
                                Todas as análises passaram a ter a aba <strong>Itens e Demandas</strong>, inclusive as simuladas — antes esses dados ficavam em um módulo separado do menu, sempre preso à última análise <em>full</em>. Agora o dado que você vê é sempre o da análise que está aberta.
                            </Paragraph>
                            <Note>
                                A aba Itens e Demandas só fica habilitada depois que a análise é totalmente concluída. Enquanto o processamento não termina, ela aparece desabilitada com o aviso de que é preciso aguardar a conclusão.
                            </Note>
                            <Figure src={imgAnalysisTabs} alt="Abas da análise" caption="Abas Resultado GDS e Itens e Demandas dentro de uma análise." />

                            <SubTitle>9.6 Consultar cálculo GDS</SubTitle>
                            <Paragraph>
                                Após a conclusão, a tela de cálculo apresenta a projeção por componente.
                            </Paragraph>
                            <Paragraph>Campos comuns:</Paragraph>
                            <SimpleTable
                                head={["Campo", "Significado"]}
                                rows={[
                                    ["Estoque inicial", "Quantidade disponível no início da análise."],
                                    ["Demanda projetada", "Consumo esperado conforme forecast e BOM."],
                                    ["Em trânsito", "Quantidade comprada, mas ainda não recebida."],
                                    ["Estoque projetado", "Saldo previsto após demandas e chegadas."],
                                    ["Status", "Indica se o item está suficiente, em risco ou faltante."],
                                ]}
                            />
                            <Paragraph>
                                Use essa visão para priorizar compras, revisar forecast, validar BOM ou antecipar riscos.
                            </Paragraph>
                            <Figure src={imgGdsCalculation} alt="Cálculo GDS" caption="Tela de cálculo GDS por componente." />

                            <SubSection id="materiais-demandas" title="9.7 Aba Itens e Demandas">
                                <Paragraph>
                                    Caminho: Geral &gt; Análise &gt; abrir uma análise &gt; aba Itens e Demandas
                                </Paragraph>
                                <Paragraph>
                                    Rota: /general/analysis/&#123;id&#125;/materiais-demandas
                                </Paragraph>
                                <Paragraph>
                                    Essa aba consolida a demanda dos componentes da análise aberta, cruzando forecast, estoque, material em trânsito e faturas pendentes. Ela é usada principalmente por compras e planejamento, porque traz um resumo por componente com a cobertura já classificada.
                                </Paragraph>
                                <Note>
                                    Itens e Demandas <strong>não é mais um módulo do menu</strong>. Antes era uma tela independente que sempre mostrava a última análise <em>full</em> concluída; hoje é uma aba de cada análise. Para comparar cenários, abra a análise desejada — inclusive uma simulada — e acesse a aba.
                                </Note>

                                <SubTitle>9.7.1 Cartões de resumo</SubTitle>
                                <Paragraph>
                                    No topo da aba, os cartões resumem a análise: itens com demanda, cobertura crítica (até 4 semanas), cobertura com atenção (5 a 8 semanas), cobertura saudável (acima de 8 semanas), itens em excesso e, quando houver, itens sem cobertura válida.
                                </Paragraph>

                                <SubTitle>9.7.2 Filtros e colunas</SubTitle>
                                <Paragraph>Os filtros disponíveis são Part Number, Tipo do Material (ZROH, ZHLB, ZMOD), BOM, Cobertura e Saldo (em excesso ou em falta). A tabela traz, por componente:</Paragraph>
                                <SimpleTable
                                    head={["Coluna", "Significado"]}
                                    rows={[
                                        ["Part Number / Descrição", "Identificação do componente."],
                                        ["Tipo do Material", "ZROH, ZHLB ou ZMOD."],
                                        ["Valor Unit.", "Último preço unitário do componente vindo do SAP."],
                                        ["Valor Total", "Preço unitário multiplicado pelo saldo do componente."],
                                        ["Modelos", "Produtos finais que consomem o componente. O número ao lado indica em quantos o item aparece."],
                                        ["Forecast", "Demanda prevista do componente no período da análise."],
                                        ["Estoque", "Quantidade física disponível."],
                                        ["Em Trânsito / Aguardando Fatura", "Quantidades compradas ainda não recebidas."],
                                        ["Saldo", "Diferença entre disponibilidade e demanda; negativo indica falta."],
                                        ["Cobertura (Semanas)", "Por quantas semanas o material disponível atende a demanda."],
                                        ["Fornecedor / Fabricantes", "Origem do componente, quando cadastrada."],
                                    ]}
                                />
                                <Paragraph>
                                    O botão <strong>Exportar</strong> gera a planilha Excel com o resultado filtrado, de forma assíncrona.
                                </Paragraph>
                                <Figure src={imgMaterialsDemands} alt="Itens e Demandas" caption="Aba Itens e Demandas dentro de uma análise." />

                                <SubTitle>9.7.3 Atualizar preços a partir do SAP</SubTitle>
                                <Paragraph>
                                    O botão <strong>Atualizar preços</strong>, ao lado da busca, consulta o SAP e grava no PlanMat o preço unitário de cada componente. É com esse dado que as colunas Preço unitário e Valor total são calculadas.
                                </Paragraph>
                                <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                    <li>Clique em <strong>Atualizar preços</strong>.</li>
                                    <li>Confirme a mensagem de confirmação.</li>
                                    <li>O botão passa a exibir <em>Atualizando preços…</em> enquanto a busca roda em segundo plano — você pode continuar navegando pelo sistema.</li>
                                    <li>Ao terminar, o sistema avisa por notificação e a data da última atualização passa a ser exibida abaixo dos filtros.</li>
                                </Box>
                                <Note>
                                    A atualização é única para todo o sistema: se outro usuário já tiver disparado uma atualização de preços, o sistema avisa que existe uma em andamento e não inicia uma nova. Os preços atualizados valem para todas as análises, não só para a que está aberta.
                                </Note>
                                <Figure src={imgMaterialsDemandsPreco} alt="Atualizar preços" caption="Botão Atualizar preços, na faixa de filtros da aba." />
                            </SubSection>

                            <SubTitle>9.8 Simulação GDS</SubTitle>
                            <Paragraph>
                                A simulação permite alterar cenários sem gravar mudanças definitivas na base oficial. A partir da tela de Resultado GDS, clique em <strong>Simular GDS</strong>: o sistema abre uma cópia da análise onde você ajusta as premissas e, ao final, clica em <strong>Reprocessar Análise</strong> para ver o impacto. <strong>Cancelar</strong> descarta a simulação.
                            </Paragraph>
                            <Paragraph>A simulação é dividida nas abas abaixo:</Paragraph>
                            <SimpleTable
                                head={["Aba", "O que permite simular"]}
                                rows={[
                                    ["Forecast", "Trocar as previsões de demanda consideradas."],
                                    ["Estoque", "Alterar a quantidade disponível dos componentes."],
                                    ["Em trânsito", "Antecipar ou ajustar chegadas de ordens de compra."],
                                    ["Período", "Mudar a janela de análise."],
                                    ["Prioridade", "Rever a prioridade de consumo dos componentes."],
                                    ["BOM (Modelos)", "Substituir componentes e criar novos itens na estrutura, por período."],
                                ]}
                            />
                            <Figure src={imgGdsSimulation} alt="Simulação GDS" caption="Tela de simulação GDS com as abas de premissas." />

                            <SubSection id="simulacao-bom" title="9.9 Aba BOM (Modelos)">
                                <Paragraph>
                                    A aba <strong>BOM (Modelos)</strong> permite mexer na estrutura do produto dentro da simulação, sem alterar a BOM oficial. Selecione o modelo de BOM no seletor à esquerda — as BOMs que já possuem alterações na simulação aparecem marcadas com um ponto azul. Use os campos Nível, Item e a busca por Part Number, descrição ou substituto para localizar o componente, e o botão <strong>Filtrar</strong> para escolher quais colunas ficam visíveis.
                                </Paragraph>
                                <Figure src={imgSimulationBom} alt="Aba BOM da simulação" caption="Aba BOM (Modelos) com a estrutura do modelo selecionado." />

                                <Paragraph>
                                    <strong>Substituir um componente:</strong> a substituição é feita na própria linha do componente.
                                </Paragraph>
                                <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                    <li>Localize a linha do componente que será substituído.</li>
                                    <li>Clique em <strong>→ adicionar substituto</strong>, na coluna de substituição da linha.</li>
                                    <li>Informe o novo Part Number, a data de início e, se a troca for temporária, a data de fim.</li>
                                    <li>Ajuste descrição, quantidade e unidade, se necessário.</li>
                                    <li>Confirme no ícone de check da própria linha.</li>
                                </Box>
                                <Paragraph>
                                    A linha passa a exibir a seta com o Part Number substituto e o período da troca. Para alterar uma substituição já cadastrada, dê <strong>duplo clique</strong> na linha; para removê-la, use o ícone de lixeira.
                                </Paragraph>
                                <Note>
                                    Só é possível cadastrar <strong>um substituto por componente</strong>. Se a data de fim ficar em branco, a substituição vale a partir da data de início até o fim do horizonte da análise.
                                </Note>
                                <Figure src={imgSimulationBomSubstituto} alt="Substituição de componente" caption="Edição em linha para cadastrar o substituto e o período da troca." />

                                <Paragraph>
                                    <strong>Criar um novo item:</strong> a criação é feita pelo menu de ações da linha, o botão de <strong>três pontos</strong> na coluna à direita.
                                </Paragraph>
                                <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                    <li>Clique nos três pontos da linha que servirá de referência.</li>
                                    <li>Escolha <strong>Adicionar acima</strong>, <strong>Adicionar abaixo</strong> ou <strong>Adicionar subitem</strong>, conforme a posição desejada na estrutura.</li>
                                    <li>Preencha o Part Number do novo item, a data de início e, se for temporário, a data de fim.</li>
                                    <li>Complete item, descrição, quantidade, unidade, tipo de material, prioridade de compra e de consumo, e marque phantom quando for o caso.</li>
                                    <li>Confirme na própria linha.</li>
                                </Box>
                                <Paragraph>
                                    Itens criados manualmente ficam identificados como <strong>Adicionado manualmente</strong> na estrutura. Para editá-los, dê duplo clique na linha; para removê-los, use <strong>Excluir</strong> no menu de três pontos. Se quiser manter o item na estrutura sem que ele entre no cálculo, use <strong>Desativar</strong> em vez de excluir.
                                </Paragraph>
                                <Note>
                                    O botão <strong>Resetar simulação</strong> remove de uma vez todos os substitutos e todos os PNs adicionados manualmente do modelo de BOM selecionado.
                                </Note>
                                <Figure src={imgSimulationBomNovoItem} alt="Criação de item na BOM" caption="Formulário do novo item, aberto pela opção Adicionar abaixo do menu de três pontos." />
                            </SubSection>

                            <SubTitle>9.10 Exportar resultados</SubTitle>
                            <Paragraph>
                                Na tela de cálculo GDS, o botão <strong>Exportar</strong> gera uma planilha Excel contendo o resultado da projeção de cobertura dos materiais. A exportação é executada de forma assíncrona, permitindo que o usuário continue navegando no sistema enquanto o arquivo é gerado e disponibilizado para download.
                            </Paragraph>
                        </SectionBlock>

                        <SectionBlock id="dashboard" title="10. Dashboard">
                            <Paragraph>
                                Caminho: Geral &gt; Dashboard
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/dashboard
                            </Paragraph>
                            <Paragraph>
                                O Dashboard é a visão gerencial de entrada do sistema. Ele foi reorganizado e passou a ter quatro blocos: os indicadores de cobertura, as ordens de compra por status, as próximas entregas e a saúde das importações SAP.
                            </Paragraph>

                            <SubTitle>10.1 Indicadores de cobertura</SubTitle>
                            <Paragraph>
                                A primeira faixa da tela traz quatro cartões de cobertura:
                            </Paragraph>
                            <SimpleTable
                                head={["Indicador", "Como interpretar"]}
                                rows={[
                                    ["Cobertura Crítica", "Itens com até 4 semanas de cobertura. Exigem ação prioritária."],
                                    ["Cobertura de Atenção", "Itens com cobertura entre 5 e 8 semanas. Devem ser acompanhados."],
                                    ["Excesso de materiais", "Componentes acima da necessidade prevista. Indicam capital parado."],
                                    ["Materiais Obsoletos", "Itens com saldo físico, mas sem vínculo com BOM ativa ou demanda relevante."],
                                ]}
                            />
                            <Paragraph>
                                Os cartões são clicáveis: os três primeiros abrem, em uma nova aba, a tela de <strong>Itens e Demandas</strong> da análise de origem já filtrada por aquela cobertura; o cartão de materiais obsoletos abre o <strong>Estoque</strong> filtrado por obsoletos.
                            </Paragraph>

                            <SubTitle>10.2 Escolher a análise dos indicadores</SubTitle>
                            <Paragraph>
                                Ao lado do título <em>Indicadores de cobertura</em> há um seletor com as análises concluídas mais recentes. Por padrão o Dashboard mostra a análise <em>full</em> mais recente; use o seletor para consultar os indicadores de outra análise concluída — é possível buscar por número, descrição ou cliente. O ícone ao lado abre a análise selecionada em uma nova aba.
                            </Paragraph>
                            <Figure src={imgDashboard} alt="Dashboard" caption="Indicadores de cobertura e seletor de análise." />

                            <SubTitle>10.3 Ordens de compra por status e próximas entregas</SubTitle>
                            <Paragraph>
                                Abaixo dos indicadores, o cartão <strong>Ordens de Compra por Status</strong> mostra a distribuição das ordens entre Aguardando fatura, Material em trânsito e Data expirada, com o total de ordens no período.
                            </Paragraph>
                            <Paragraph>
                                Ao lado dele, o widget <strong>Próximas Entregas</strong> lista as chegadas previstas com Part Number, descrição, PO, fatura, quantidade, data e status. Em ambos, o link <strong>Ver todas</strong> leva à tela completa de Ordens de Compra.
                            </Paragraph>
                            <Figure src={imgDashboardWidgets} alt="Widgets do Dashboard" caption="Cartão de ordens de compra por status e widget de próximas entregas." />

                            <SubSection id="saude-sap" title="10.4 Saúde das importações SAP">
                            <Paragraph>
                                O último bloco do Dashboard acompanha o resultado da <strong>última execução</strong> de cada carga vinda do SAP. Ele responde, sem sair da tela, à pergunta que costuma vir antes de confiar em uma análise: os dados de origem estão atualizados?
                            </Paragraph>
                            <SimpleTable
                                head={["Carga", "O que alimenta", "Para onde leva o Ver detalhes"]}
                                rows={[
                                    ["BOM", "Estruturas de produto usadas na explosão da demanda.", "Importar via SAP, aba BOM."],
                                    ["Estoque e Ordens de Compra", "Saldos físicos, material em trânsito e ordens em aberto.", "Importar via SAP, aba Estoque."],
                                    ["Forecast", "Demanda prevista por modelo e período.", "Importar via SAP, aba Forecast."],
                                    ["Preço por PN", "Preço unitário dos componentes.", "Aba Itens e Demandas da análise selecionada no seletor acima."],
                                ]}
                            />
                            <Paragraph>
                                Cada cartão traz a data e hora da última execução, a duração e uma mensagem de resultado. Quando a carga termina em erro, a mensagem exibida é a devolvida pelo próprio SAP, o que ajuda a distinguir uma falha de conexão de um problema nos dados.
                            </Paragraph>
                            <SimpleTable
                                head={["Situação", "O que significa"]}
                                rows={[
                                    ["Saudável", "A última execução terminou com sucesso, total ou parcialmente."],
                                    ["Crítico", "A última execução terminou com erro. Os dados daquela carga podem estar desatualizados."],
                                    ["Processando", "A carga está rodando agora. O bloco se atualiza sozinho a cada 15 segundos até terminar."],
                                    ["Sem execução", "Nenhuma execução registrada para essa carga."],
                                ]}
                            />
                            <Note>
                                Quando qualquer uma das quatro cargas está em situação crítica, um selo <strong>Crítico</strong> aparece ao lado do título do bloco — é o alerta de que vale conferir as importações antes de rodar ou interpretar uma análise.
                            </Note>
                            <Figure src={imgDashboardSap} alt="Saúde das importações SAP" caption="Situação da última execução das quatro cargas SAP." />
                            </SubSection>

                            <Note>
                                Os gráficos de previsão contínua, tendência e outliers, que antes ficavam no Dashboard, foram movidos para a página <strong>Previsão de demanda por IA</strong> — veja a seção 11.
                            </Note>
                        </SectionBlock>

                        <SectionBlock id="previsao-ia" title="11. Previsão de demanda por IA">
                            <Paragraph>
                                Caminho: Geral &gt; Previsão de demanda por IA
                            </Paragraph>
                            <Paragraph>
                                Rota: /general/ai-demand-forecast
                            </Paragraph>
                            <Paragraph>
                                Essa página reúne a análise estatística da demanda: a previsão contínua com detecção de outliers e intervalo de confiança de 95%, comparada ao histórico consolidado e ao forecast informado pelo cliente.
                            </Paragraph>

                            <SubTitle>11.1 Consultar a previsão</SubTitle>
                            <Box component="ol" sx={{ pl: 4, mb: 2 }}>
                                <li>Escolha em <strong>Filtrar por</strong> se a consulta será por Part Number ou por Modelo.</li>
                                <li>Selecione o item desejado no campo ao lado.</li>
                                <li>Clique na lupa para carregar o gráfico.</li>
                            </Box>
                            <Paragraph>O gráfico sobrepõe as séries:</Paragraph>
                            <SimpleTable
                                head={["Série", "Significado"]}
                                rows={[
                                    ["Forecast consolidado", "Histórico de demanda já realizado."],
                                    ["Forecast previsto IA", "Projeção calculada pelo modelo estatístico."],
                                    ["Forecast previsto Dell", "Previsão informada pelo cliente."],
                                    ["Fora do Padrão", "Pontos identificados como outliers no histórico."],
                                    ["Tendência", "Direção geral da demanda no período."],
                                    ["IC 95%", "Faixa de confiança da projeção."],
                                ]}
                            />

                            <SubTitle>11.2 Cartões de leitura rápida</SubTitle>
                            <Paragraph>
                                À direita do gráfico, três cartões resumem os últimos 5 meses: <strong>Previsão de longo prazo</strong> (variação da demanda), <strong>Desvios/Anomalias</strong> (quantidade de ocorrências fora do padrão) e <strong>Tendência</strong> (alta, baixa ou neutra).
                            </Paragraph>
                            <Note>
                                A previsão por IA é uma ferramenta de apoio à leitura da demanda. O cálculo de disponibilidade que orienta compras continua sendo o da Análise GDS.
                            </Note>
                            <Figure src={imgAiForecast} alt="Previsão de demanda por IA" caption="Gráfico de previsão contínua com detecção de outliers e cartões de resumo." />
                        </SectionBlock>

                        <SectionBlock id="boas-praticas" title="12. Boas práticas de uso">
                            <Paragraph>
                                Para reduzir erros e melhorar a qualidade das análises:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Confira se os dados importados estão na versão correta.</li>
                                <li>Valide BOMs antes de rodar análises críticas.</li>
                                <li>Evite criar várias análises iguais sem necessidade.</li>
                                <li>Use nomes padronizados para forecasts.</li>
                                <li>Revise o relatório de erros antes de acionar suporte.</li>
                                <li>Não compartilhe senha.</li>
                                <li>Sempre faça logout ao terminar o uso em computador compartilhado.</li>
                                <li>Em caso de dúvida sobre dados oficiais, consulte a origem responsável antes de alterar cadastros.</li>
                            </Box>
                        </SectionBlock>

                        <SectionBlock id="faq" title="13. Perguntas frequentes">
                            <SubTitle>Por que minha análise demora muito?</SubTitle>
                            <Paragraph>
                                Análises com muitos componentes, janelas longas ou muitas versões de forecast podem levar mais tempo. Se houver fila de processamento, sua análise aguardará a conclusão das anteriores.
                            </Paragraph>

                            <SubTitle>Importei a planilha, mas alguns componentes não aparecem. O que verificar?</SubTitle>
                            <Paragraph>
                                Verifique se o part number não importado segue o mesmo padrão dos part numbers importados.
                            </Paragraph>

                            <SubTitle>A importação do SAP falhou. O que fazer?</SubTitle>
                            <Paragraph>
                                Confirme se a VPN ou rede necessária está ativa. Se o erro continuar, registre o ID da execução e acione suporte ou infraestrutura.
                            </Paragraph>

                            <SubTitle>O que é um componente phantom?</SubTitle>
                            <Paragraph>
                                É um componente sem estoque próprio. Quando a análise encontra um phantom, ela resolve a demanda usando os componentes filhos da BOM.
                            </Paragraph>

                            <SubTitle>Posso consultar uma análise antiga?</SubTitle>
                            <Paragraph>
                                Sim. Acesse o módulo Análise, abra a análise desejada e consulte o cálculo ou exporte novamente o resultado, se disponível.
                            </Paragraph>

                            <SubTitle>Como apago uma análise?</SubTitle>
                            <Paragraph>
                                Na lista de Análise, selecione uma ou mais linhas e clique em Deletar. Confirme apenas se a exclusão for realmente necessária.
                            </Paragraph>

                            <SubTitle>Onde foi parar o menu Materiais e Demandas?</SubTitle>
                            <Paragraph>
                                Ele deixou de ser um item do menu e virou a aba <strong>Itens e Demandas</strong> dentro de cada análise. Abra Análise, entre na análise desejada e use a aba ao lado de Resultado GDS.
                            </Paragraph>

                            <SubTitle>A aba Itens e Demandas está desabilitada. Por quê?</SubTitle>
                            <Paragraph>
                                A aba só é liberada quando a análise fica totalmente concluída. Se a análise ainda estiver processando ou finalizando, aguarde a conclusão.
                            </Paragraph>

                            <SubTitle>Cliquei em Atualizar preços e o sistema disse que já existe uma atualização em andamento.</SubTitle>
                            <Paragraph>
                                A busca de preços no SAP é única para todo o sistema. Se outra pessoa já disparou a atualização, aguarde a conclusão — o resultado vale para todas as análises.
                            </Paragraph>

                            <SubTitle>A substituição que fiz na simulação altera a BOM oficial?</SubTitle>
                            <Paragraph>
                                Não. Substituições e itens criados na aba BOM (Modelos) valem apenas dentro daquela simulação e no período informado. A BOM cadastrada no módulo BOM permanece intacta.
                            </Paragraph>

                            <SubTitle>Como altero minha senha?</SubTitle>
                            <Paragraph>
                                Solicite apoio a um administrador, conforme a política interna de acesso. O sistema não possui recuperação de senha pelo próprio usuário.
                            </Paragraph>
                        </SectionBlock>

                        <SectionBlock id="glossario" title="14. Glossário">
                            <SimpleTable
                                head={["Termo", "Significado"]}
                                rows={[
                                    ["BOM", "Estrutura de materiais de um produto final."],
                                    ["GDS", "Cálculo que projeta estoque, demanda e chegadas ao longo do tempo."],
                                    ["Forecast", "Previsão de demanda por cliente, componente ou período."],
                                    ["Phantom", "Componente sem estoque próprio, resolvido pelos filhos da BOM."],
                                    ["Item alternativo", "Componente que pode substituir outro na mesma posição da estrutura."],
                                    ["PO", "Ordem de compra com itens em trânsito."],
                                    ["On hand", "Quantidade fisicamente disponível em estoque."],
                                    ["In transit", "Quantidade comprada, mas ainda não recebida."],
                                    ["Projected inventory", "Estoque projetado para data futura."],
                                    ["Outlier", "Valor fora do padrão esperado."],
                                    ["Cobertura", "Por quanto tempo o material disponível atende a demanda prevista."],
                                    ["Preço unitário", "Valor unitário do componente trazido do SAP e gravado no PlanMat."],
                                    ["ZROH / ZHLB / ZMOD", "Tipos de material: matéria-prima, produto semiacabado e modelo/phantom."],
                                    ["IC 95%", "Intervalo de confiança de 95% usado na previsão por IA."],
                                ]}
                            />
                        </SectionBlock>

                        <SectionBlock id="suporte" title="15. Suporte">
                            <Paragraph>
                                Quando precisar de suporte, envie as informações abaixo:
                            </Paragraph>
                            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                                <li>Ambiente utilizado, por exemplo produção ou homologação.</li>
                                <li>Usuário impactado.</li>
                                <li>Módulo ou tela onde ocorreu o problema.</li>
                                <li>Passo a passo do que foi feito.</li>
                                <li>Mensagem de erro exibida.</li>
                                <li>ID da análise ou importação, quando houver.</li>
                                <li>Print da tela, se possível.</li>
                                <li>Horário aproximado da ocorrência.</li>
                            </Box>
                            <Paragraph>
                                <strong>Canal de atendimento:</strong> a definir.
                            </Paragraph>
                            <Paragraph>
                                <strong>SLA de resposta:</strong> a definir.
                            </Paragraph>
                        </SectionBlock>
                    </Paper>
                </Grid>
            </Grid>
        </section>
    );
};

export default HelpComponent;
